import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Mail, Clock, Send, MessageSquare } from 'lucide-react';
import { trackCta } from '../analytics';
import { ScrollReveal, StaggerReveal, StaggerItem } from './ScrollReveal';
import BookDemoButton from './BookDemoButton';
import './Contact.css';

const Contact = () => {
  const reduceMotion = useReducedMotion();
  const appBaseUrl = process.env.REACT_APP_APP_URL || 'https://app.pycrust.io';
  const signupUrl = `${appBaseUrl}/login`;
  const contactInfo = [
    {
      icon: <Mail className="ui-icon-5xl" />,
      title: "Email Us",
      details: "contact@pycrust.io",
      description: "Billing, product questions, or partnerships",
    },
    {
      icon: <MessageSquare className="ui-icon-5xl" />,
      title: "Enterprise",
      details: "Custom setups",
      description: "Larger teams or non-standard requirements",
    },
    {
      icon: <Clock className="ui-icon-5xl" />,
      title: "Response Time",
      details: "Within 24 Hours",
      description: "We reply to every message",
    },
    {
      icon: <Send className="ui-icon-5xl" />,
      title: "Get Started",
      details: "First client free",
      description: "No paid plan required for your first client workspace",
    },
  ];

  return (
    <section id="contact" className="contact">
      <div className="contact-mesh" aria-hidden />
      <div className="container">
        <ScrollReveal direction="up" className="section-header">
          <div className="section-badge">
            <span>Contact</span>
          </div>
          <h2 className="section-title">Talk to the team</h2>
          <p className="section-subtitle">
            Workspaces, billing, enterprise setups: we answer the same inbox.
          </p>
        </ScrollReveal>

        <div className="contact-content">
          <div className="contact-info-section">
            <ScrollReveal direction="up" delay={0.05}>
              <p className="contact-intro">
                Ask how workspaces fit your team, report a bug, or tell us what you need next. We read everything.
              </p>
            </ScrollReveal>

            <StaggerReveal className="contact-info-grid">
              {contactInfo.map((info, index) => (
                <StaggerItem key={info.title}>
                  <motion.article
                    className="contact-info-card"
                    whileHover={reduceMotion ? undefined : { y: -5 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className={`info-icon info-icon-${index}`}>
                      {info.icon}
                    </div>
                    <div className="info-content">
                      <h4>{info.title}</h4>
                      <p className="info-details">{info.details}</p>
                      <p className="info-description">{info.description}</p>
                    </div>
                  </motion.article>
                </StaggerItem>
              ))}
            </StaggerReveal>

            <ScrollReveal direction="up" delay={0.1}>
              <div className="contact-cta">
                <h4>Open a workspace</h4>
                <p>
                  Upload assets, connect accounts, and invite collaborators. Your first client workspace stays free.
                </p>
                <div className="cta-actions-row cta-actions-row--center">
                  <motion.button
                    className="btn btn-primary"
                    whileHover={reduceMotion ? undefined : { scale: 1.03, y: -2 }}
                    whileTap={reduceMotion ? undefined : { scale: 0.98 }}
                    onClick={() => {
                      trackCta('get_started', 'contact_page');
                      window.open(signupUrl, '_blank');
                    }}
                  >
                    <Send className="ui-icon-xl" />
                    Get started
                  </motion.button>
                  <BookDemoButton placement="contact_page" />
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
